/**
 * The screen that opens a session: ask the platform, then hand everything to the console.
 *
 * # It issues exactly once
 *
 * `POST .../session` mints a rendezvous token, and the response carries the only copy of it. A
 * screen that re-issued on every render would leave a trail of unused single-use tokens and an
 * audit ledger claiming an engineer opened forty sessions to do one job. So the call is made once
 * on mount, keyed by the request identifier, and a retry is a deliberate act by the person.
 *
 * # What this file reads out of the contract, and what it refuses to
 *
 * It reads `capabilities.input_control`, unverified, to decide whether the terminal accepts
 * typing. That is safe **because it decides nothing**: the endpoint holds the same contract and
 * drops keystrokes it does not permit, so a platform that lied here would produce a terminal that
 * accepts typing nobody acts on. Disabling input is a courtesy that turns a confusing silence into
 * a visible "view only".
 *
 * It does **not** read the endpoint key, and could not usefully: `SessionConsole` passes the whole
 * contract to WebAssembly, which verifies the client's signature and extracts that key itself
 * (ADR-0020). A `JSON.parse` here to pull it out would be the entire attack, performed by our own
 * code with the best of intentions.
 */

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api, type IssuedSession } from '../shared/api/client';
import type { PresentedTicket } from '../shared/session/client';
import { Card, ErrorBox, Formula, PageHeader, Spinner } from '../shared/ui/ui';
import { SessionConsole } from './SessionConsole';

/** Decodes the hex token the API returns. */
function unhex(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(text.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/** Whether the contract grants typing. Unverified and deliberately non-authoritative — see above. */
function grantsInput(document: Record<string, unknown>): boolean {
  const capabilities = document.capabilities;
  if (typeof capabilities !== 'object' || capabilities === null) return false;
  return (capabilities as Record<string, unknown>).input_control === true;
}

export function Session(): JSX.Element {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();

  const [issued, setIssued] = useState<IssuedSession | null>(null);
  const [ticket, setTicket] = useState<PresentedTicket | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [ended, setEnded] = useState<string | null>(null);

  useEffect(() => {
    if (!requestId) return;
    let abandoned = false;

    // The identity is generated BEFORE the request, because the platform signs a ticket over its
    // public half and the private half has to still be here when the ticket comes back. It lives
    // in this closure and in component state, never in storage: a key that outlived the tab would
    // be a key worth stealing, and this one is worth nothing a minute after the session ends.
    void (async (): Promise<void> => {
      const { default: init, ConsoleIdentity } = await import('../wasm/conduit_console');
      await init();
      const identity = ConsoleIdentity.generate();

      return api
        .openSession(requestId, identity.publicKeyHex)
        .then((session) => {
          // A response arriving after the engineer navigated away is a token nobody will use. It
          // cannot be handed back, so the honest thing is to let it expire in two minutes rather
          // than render a console for a screen that is gone.
          if (abandoned) return;
          setIssued(session);

          // Absent means the deployment issues no tickets, and the rendezvous stays a bearer
          // token. Not an error: the relay's open mode accepts exactly that, and inventing one
          // here would break every deployment that has not rolled this out.
          if (session.ticket) {
            setTicket({
              identity,
              remoteSessionId: session.ticket.remote_session_id,
              consolePublicKey: session.ticket.console_public_key,
              expiresAt: session.ticket.expires_at,
              signature: session.ticket.signature,
            });
          }
        })
        .catch((err: unknown) => {
          if (!abandoned) setError(err);
        });
    })().catch((err: unknown) => {
      if (!abandoned) setError(err);
    });

    return (): void => {
      abandoned = true;
    };
  }, [requestId]);

  const onEnded = useCallback((reason: string): void => {
    setEnded(reason);
  }, []);

  if (!requestId) {
    return <ErrorBox error={new Error('no access request was named')} />;
  }

  return (
    <>
      <PageHeader
        title="Remote session"
        subtitle="End to end encrypted to the client's endpoint. The contract decides what it may do."
      />

      <ErrorBox error={error} />

      {ended !== null && (
        <Card title="Session ended">
          <p>{ended}</p>
          <button
            className="button"
            type="button"
            onClick={() => {
              void navigate('/access-requests');
            }}
          >
            Back to access requests
          </button>
        </Card>
      )}

      {issued === null && error === null && <Spinner label="Issuing a rendezvous…" />}

      {issued !== null && ended === null && (
        <>
          <Card title="What was signed">
            <Formula>
              contract verified by key {issued.contract.key_fingerprint} · session{' '}
              {issued.remote_session_id.slice(0, 8)}
            </Formula>
          </Card>

          <SessionConsole
            relayUrl={issued.relay_url}
            rendezvousToken={unhex(issued.rendezvous_token)}
            contract={{
              document: issued.contract.document,
              signatureHex: issued.contract.signature_hex,
              verifyingKeyHex: issued.contract.verifying_key_hex,
            }}
            ticket={ticket}
            mayType={grantsInput(issued.contract.document)}
            onEnded={onEnded}
          />
        </>
      )}
    </>
  );
}
